import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Plus, Users } from 'lucide-react';
import { turmaService } from '../../shared/services';
import { usePermissions } from '../../app/hooks/usePermissions';

function mensagemDeErro(error: unknown, padrao: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) return data.message;
  }
  return padrao;
}

export function CohortsPage() {
  const { isAdmin, isInstrutor } = usePermissions();
  const staff = isAdmin || isInstrutor;
  const queryClient = useQueryClient();
  const [mostrarForm, setMostrarForm] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [form, setForm] = useState({ nome: '', descricao: '', dataInicio: '', dataFim: '' });

  const { data: turmas = [], isLoading } = useQuery({
    queryKey: ['turmas', staff],
    queryFn: staff ? turmaService.getTodas : turmaService.getMinhas,
  });

  const criar = useMutation({
    mutationFn: () =>
      turmaService.criar({
        nome: form.nome,
        descricao: form.descricao || undefined,
        dataInicio: new Date(`${form.dataInicio}T00:00:00`).toISOString(),
        dataFim: form.dataFim ? new Date(`${form.dataFim}T00:00:00`).toISOString() : null,
        ativo: true,
      }),
    onSuccess: () => {
      setForm({ nome: '', descricao: '', dataInicio: '', dataFim: '' });
      setMostrarForm(false);
      setErro(null);
      queryClient.invalidateQueries({ queryKey: ['turmas'] });
    },
    onError: (error) => setErro(mensagemDeErro(error, 'Não foi possível criar a turma.')),
  });

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{staff ? 'Turmas' : 'Minhas Turmas'}</h1>
            <p className="text-gray-600 mt-2">
              {staff
                ? 'Crie turmas, acompanhe alunos e abra o detalhe para matricular participantes.'
                : 'Acompanhe suas turmas e conteúdos.'}
            </p>
          </div>

          {staff && (
            <button
              type="button"
              onClick={() => setMostrarForm((valor) => !valor)}
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={16} /> Nova turma
            </button>
          )}
        </div>

        {erro && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{erro}</div>}

        {mostrarForm && (
          <form
            className="card mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              setErro(null);
              criar.mutate();
            }}
          >
            <div className="md:col-span-2">
              <label htmlFor="turma-nome" className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input
                id="turma-nome"
                value={form.nome}
                onChange={(event) => setForm({ ...form, nome: event.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
                minLength={2}
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="turma-desc" className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
              <textarea
                id="turma-desc"
                value={form.descricao}
                onChange={(event) => setForm({ ...form, descricao: event.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows={2}
              />
            </div>
            <div>
              <label htmlFor="turma-inicio" className="block text-sm font-medium text-gray-700 mb-1">Início</label>
              <input
                id="turma-inicio"
                type="date"
                value={form.dataInicio}
                onChange={(event) => setForm({ ...form, dataInicio: event.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
              />
            </div>
            <div>
              <label htmlFor="turma-fim" className="block text-sm font-medium text-gray-700 mb-1">Fim (opcional)</label>
              <input
                id="turma-fim"
                type="date"
                value={form.dataFim}
                onChange={(event) => setForm({ ...form, dataFim: event.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div className="md:col-span-2">
              <button type="submit" disabled={criar.isPending} className="btn-primary disabled:opacity-60">
                {criar.isPending ? 'Criando...' : 'Criar turma'}
              </button>
            </div>
          </form>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : turmas.length === 0 ? (
          <div className="card text-center py-12 text-gray-600">
            {staff ? 'Nenhuma turma cadastrada.' : 'Você ainda não está matriculado em nenhuma turma.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {turmas.map((turma) => (
              <Link key={turma.id} to={`/turmas/${turma.id}`} className="card hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Users className="text-primary-600" size={20} />
                  </div>
                  {turma.ativo && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">Ativa</span>
                  )}
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2">{turma.nome}</h3>

                {turma.descricao && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{turma.descricao}</p>
                )}

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Calendar size={16} />
                    <span>Início: {new Date(turma.dataInicio).toLocaleDateString('pt-BR')}</span>
                  </div>
                  {turma.dataFim && (
                    <div className="flex items-center gap-2">
                      <Calendar size={16} />
                      <span>Fim: {new Date(turma.dataFim).toLocaleDateString('pt-BR')}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Users size={16} />
                    <span>{turma.totalAlunos} aluno(s)</span>
                  </div>
                  <p className="text-xs text-gray-500">Instrutor: {turma.instructorName}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
